// App — Strata shell.
//
// Sidebar + main column + toast portal + dev StrataTweaks. The router's
// <Outlet /> renders the active page inside <main>. Nav variant is driven by
// the EFFECTIVE role (real role from useMe, optionally overridden by the dev
// tweaks panel) — never use this for actual authorization; the server enforces.
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Avatar, ToastStack } from "@/components/UI";
import { MerchantSwitcher } from "@/components/MerchantSwitcher";
import { MerchantStatusBanner } from "@/components/MerchantStatusBanner";
import { StrataTweaks } from "@/components/StrataTweaks";
import { useAuth, effectiveRole, isAdminRole, isSuperadminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useMe } from "@/queries/useMe";
import { useMerchants } from "@/queries/useMerchants";
import { hexToOklch } from "@/utils/color";

interface NavItem {
  key: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  badge?: number;
}

const CRUMB_MAP: Record<string, string> = {
  "/": "Overview",
  "/merchants": "Merchants",
  "/payouts": "Payouts",
  "/approvals": "Approvals",
  "/beneficiaries": "Beneficiaries",
  "/banks": "Bank rails",
  "/ledger": "Ledger",
  "/keys": "API keys",
  "/keys/docs": "API reference",
  "/keys/webhooks": "Webhooks",
  "/rbac": "Users & roles",
  "/platform/rails": "Platform rails",
  "/platform/payouts": "Platform payouts",
  "/admin/provider-logs": "Provider logs",
  "/admin/rails": "All bank rails",
  "/admin/audit-log": "Audit log",
  "/customers": "Customers",
  "/reports": "Reports",
  "/fees": "Fees",
  "/manual-payouts": "Manual payouts",
  "/settings": "Settings",
};

export function App() {
  useMe(); // hydrate user store
  const { data: merchants } = useMerchants(); // admin-only; gated inside the hook
  const location = useLocation();
  const navigate = useNavigate();

  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const qc = useQueryClient();
  // SUBSCRIBE to the effective role via a selector — the previous
  // `effectiveRole(useAuth.getState())` snapshot only re-evaluated on
  // unrelated re-renders. When StrataTweaks flipped `devRole` (or `user.role`
  // changed without `user` itself changing reference), the sidebar nav variant
  // stayed stale. Zustand re-runs the selector on every state change and
  // only re-renders when the returned string actually changes (Object.is).
  const role = useAuth((s) => effectiveRole(s));
  const theme = useUI((s) => s.theme);
  const density = useUI((s) => s.density);
  const accent = useUI((s) => s.accent);
  const toasts = useUI((s) => s.toasts);

  // Apply tokens to <html> on first render and whenever ui state changes.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.density = density;
    document.documentElement.style.setProperty("--accent", hexToOklch(accent));
  }, [theme, density, accent]);

  const isAdmin = isAdminRole(role);
  // Fees is superadmin-only — see isSuperadminRole for why this one surface
  // does NOT follow the admin/superadmin equivalence used everywhere else.
  // Reads the REAL role, never the dev override: StrataTweaks must not be
  // able to conjure a pricing screen.
  const isSuperadmin = useAuth((s) => isSuperadminRole(s.user?.role));

  // Per-merchant membership role + API toggle for the currently-selected
  // merchant. Used to surface the API-keys nav entry to operator+
  // merchants ONLY when the superadmin has turned API access on for this
  // merchant (backend migration 027). Admin always sees the entry — they
  // can reach a disabled merchant's API surface to inspect history.
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const myMembership = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === currentMerchantId),
  );
  const myMembershipRole = myMembership?.role;
  const merchantAPIEnabled = myMembership?.api_enabled ?? false;
  const canManageKeys =
    isAdmin ||
    ((myMembershipRole === "owner" || myMembershipRole === "operator") &&
      merchantAPIEnabled);
  // Webhooks track the same gate as API keys (operator+ on the backend
  // — RequireMembershipRole operator chain). The two surfaces move
  // together: anyone who can mint a key can also register the
  // callback the integration that uses the key will listen on.
  const canManageWebhooks = canManageKeys;

  // NOTE: the shortcut into the selected merchant (page + settings modal)
  // deliberately does NOT live here. It hangs off MerchantSwitcher in the
  // header, which is the one merchant-context control present on every
  // screen — a sidebar row would be one more thing to scan for a
  // destination the operator is already looking at the name of.

  const navItems: NavItem[] = isAdmin
    ? [
        { key: "dashboard", to: "/", label: "Overview", icon: I.Dashboard },
        { key: "merchants", to: "/merchants", label: "Merchants", icon: I.Merchants, badge: merchants?.length },
        // Migration 019 — admin approval queue, sits prominently below
        // Merchants because it's where admins spend most of their time
        // once any merchant uploads a batch.
        { key: "approvals", to: "/approvals", label: "Approvals", icon: I.Check },
        { key: "payouts", to: "/payouts", label: "Payouts", icon: I.Payouts },
        { key: "beneficiaries", to: "/beneficiaries", label: "Beneficiaries", icon: I.Users },
        // PDF round 2 item 5: customer directory. Sits between
        // Beneficiaries (bank-account rows) and Bank rails (funding
        // accounts) because a customer is one tier "above" a bene —
        // one customer can have many beneficiaries (= bank accounts).
        { key: "customers", to: "/customers", label: "Customers", icon: I.Users },
        { key: "banks", to: "/banks", label: "Bank rails", icon: I.Banks },
        // Ledger for the switcher-selected merchant. Previously reachable
        // only by drilling /merchants → a merchant → Ledger, which meant
        // the balance behind the dashboard card had no direct path.
        { key: "ledger", to: "/ledger", label: "Ledger", icon: I.Wallet },
        // Reports — first item in the Develop & control group. Statement /
        // audit / reconciliation downloads land here for any merchant the
        // admin is reviewing (the page reads the X-Merchant-ID-selected
        // merchant the same way the dashboard does).
        { key: "reports", to: "/reports", label: "Reports", icon: I.Payouts },
        { key: "keys", to: "/keys", label: "API keys", icon: I.Keys },
        { key: "webhooks", to: "/keys/webhooks", label: "Webhooks", icon: I.Bell },
        { key: "rbac", to: "/rbac", label: "Users & roles", icon: I.Users },
        // Phase 8 — admin-only platform tier. Live in the develop group
        // (below operateCount=5 for admin) since they're operator-tier
        // infrastructure, not per-merchant operations.
        { key: "platform-rails", to: "/platform/rails", label: "Platform rails", icon: I.Banks },
        { key: "platform-payouts", to: "/platform/payouts", label: "Platform payouts", icon: I.Payouts },
        // Admin-only diagnostics surface — every outbound upstream rail call
        // captured server-side. Sits at the end of the develop group
        // because it's an operator triage tool, not a daily-ops surface.
        { key: "provider-logs", to: "/admin/provider-logs", label: "Provider logs", icon: I.Keys },
        // PDF round 3 N5 — cross-tenant view of every bank rail with the
        // credential triple shown obfuscated (`f****l`). Operator surface
        // for spotting two merchants sharing the same IDFC credentials.
        { key: "admin-rails", to: "/admin/rails", label: "All bank rails", icon: I.Banks },
        // Admin-only audit log viewer — cross-tenant "who did what,
        // from where, when" with before/after JSON inspection.
        { key: "admin-audit", to: "/admin/audit-log", label: "Audit log", icon: I.Users },
        // Razorpay payment-downtimes board (admin-only). Polls
        // api.razorpay.com via the backend BFF so the key+secret
        // never touch the browser.
        { key: "provider-status", to: "/admin/provider-status", label: "Provider status", icon: I.Bell },
        // Per-merchant fee scales. Conditional, so it sits in the develop
        // group — an entry that appears and disappears must not fall inside
        // the positional operate slice below.
        ...(isSuperadmin
          ? [
              { key: "fees", to: "/fees", label: "Fees", icon: I.Wallet } as NavItem,
              // Manual rail. ONE entry for both the payment files and the CSV
              // layouts that shape them — the page tabs between them rather
              // than the sidebar growing twice for one feature.
              {
                key: "manual-payouts",
                to: "/manual-payouts",
                label: "Manual payouts",
                icon: I.Banks,
              } as NavItem,
            ]
          : []),
        // Account & security — passkey enrolment + management. Self-scoped
        // to the signed-in operator, so it's available to every role.
        { key: "settings", to: "/settings", label: "Settings", icon: I.Settings },
      ]
    : [
        { key: "dashboard", to: "/", label: "Overview", icon: I.Dashboard },
        { key: "payouts", to: "/payouts", label: "Payouts", icon: I.Payouts },
        // Merchants previously had no path to register a beneficiary at
        // all — the Add button lived on /merchants/:id, which they could
        // not navigate to from the sidebar. Surfacing /beneficiaries here
        // restores parity with the admin sidebar.
        { key: "beneficiaries", to: "/beneficiaries", label: "Beneficiaries", icon: I.Users },
        // PDF round 2 item 5: customer directory also exposed to
        // merchants — they manage their own customer book.
        { key: "customers", to: "/customers", label: "Customers", icon: I.Users },
        { key: "banks", to: "/banks", label: "My accounts", icon: I.Banks },
        // Ledger — read-only for merchants (LedgerEntryForm is admin-only
        // inside the page), but it's the statement behind their balance
        // card, so it belongs in the operate group next to My accounts.
        { key: "ledger", to: "/ledger", label: "Ledger", icon: I.Wallet },
        // Reports — first develop-group item for merchants too. CSV /
        // JSON / XLSX downloads of the merchant's own statement, audit
        // and reconciliation views; the backend pins scope to the
        // caller's merchant via X-Merchant-ID / API-key binding.
        { key: "reports", to: "/reports", label: "Reports", icon: I.Payouts },
        // M2M API is now broadly available. The link is surfaced to
        // merchant owner/operator only — viewers can't mint (operator+
        // backend gate) so the sidebar dead-end is hidden from them.
        ...(canManageKeys
          ? [
              {
                key: "keys",
                to: "/keys",
                label: "API keys",
                icon: I.Keys,
              } as NavItem,
            ]
          : []),
        ...(canManageWebhooks
          ? [
              {
                key: "webhooks",
                to: "/keys/webhooks",
                label: "Webhooks",
                icon: I.Bell,
              } as NavItem,
            ]
          : []),
        { key: "rbac", to: "/rbac", label: "Team", icon: I.Users },
        // Account & security — passkey enrolment + management.
        { key: "settings", to: "/settings", label: "Settings", icon: I.Settings },
      ];

  // Operate group keeps the daily-ops items at the top. These counts are
  // positional — they slice navItems above, so inserting an entry into
  // either array means bumping the matching count here.
  // Admin operate group (8): dashboard, merchants, approvals, payouts,
  //   beneficiaries, customers, banks, ledger.
  // Merchant operate group (6): dashboard, payouts, beneficiaries,
  //   customers, banks, ledger — no approvals (admin-only), no keys
  //   (hidden per PDF review round 1).
  const operateCount = isAdmin ? 8 : 6;
  const operateItems = navItems.slice(0, operateCount);
  const developItems = navItems.slice(operateCount);

  const crumb = CRUMB_MAP[location.pathname] ?? "Detail";
  const displayName = user?.email?.split("@")[0] ?? "Operator";
  const roleLabel = isAdmin ? "Platform Admin" : user?.email ? user.email : "Merchant";

  return (
    <>
      <div className="app" data-screen-label={"console-" + location.pathname.slice(1)}>
        <aside className="sidebar">
          <div className="brand">
            {/* Sphere Payouts wordmark + logo (rebrand from Strata).
                The PNG is the sphere icon; pair with the wordmark. */}
            <img
              src="/sphere-logo.png"
              alt=""
              className="brand-mark"
              aria-hidden="true"
            />
            <span className="brand-name">SPHERE PAYOUTS</span>
            <span className={"brand-env " + (isAdmin ? "live" : "")}>
              {isAdmin ? "Admin" : "Merchant"}
            </span>
          </div>

          <div className="nav-section">Operate</div>
          <nav className="stack-2">
            {operateItems.map((it) => (
              <NavItemLink key={it.key} item={it} />
            ))}
          </nav>

          <div className="nav-section">Develop &amp; control</div>
          <nav className="stack-2">
            {developItems.map((it) => (
              <NavItemLink key={it.key} item={it} />
            ))}
          </nav>

          <div className="sidebar-foot">
            <div className="user-chip">
              <Avatar name={displayName} size={28} />
              <div className="meta">
                <div className="n">{displayName}</div>
                <div className="r">{roleLabel}</div>
              </div>
              <button
                className="icon-btn"
                type="button"
                title="Sign out"
                aria-label="Sign out"
                onClick={async () => {
                  await logout();
                  // Drop every cached query so the next login (potentially a
                  // different user) cannot see the previous account's
                  // merchant data, payouts, API keys, etc., between mount
                  // and refetch.
                  qc.clear();
                  navigate("/login", { replace: true });
                }}
              >
                <I.ArrowRight size={12} />
              </button>
            </div>
          </div>
        </aside>

        <main className="main">
          <header className="header">
            <div className="crumbs">
              <span>Console</span>
              <span className="sep">/</span>
              <span className="here">{crumb}</span>
            </div>
            <div className="spacer" />
            <MerchantSwitcher />
            {/* Removed (dead chrome): global search input + ⌘K hint (no
                search backend), Bell button with red dot (no notification
                system), Globe button (no i18n surface). Add back when each
                feature is real. */}
          </header>

          {/* PDF round 2: suspended/closed merchant banner. Hidden when
              status is active OR no merchant is selected. Sits between
              the header and the routed page content so it's visible on
              every authed surface. */}
          <MerchantStatusBanner />

          <Outlet />
        </main>
      </div>

      {import.meta.env.DEV && <StrataTweaks />}
      <ToastStack items={toasts} />
    </>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
    >
      <item.icon />
      <span>{item.label}</span>
      {item.badge != null && <span className="badge">{item.badge}</span>}
    </NavLink>
  );
}
